import { call, put, select, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import factory from "utils/getApiHandlerEffect";
import { selectActivationCode, selectUserCidpId } from "scenes/selectors";
import { mfaFlow } from "utils/enum";
import { SagaIterator } from "redux-saga";
import {
  afterOktaAuthenticatorFetch,
  afterPassCodeSubmission,
  afterStatusFetch,
  loadQRCode,
  loadStatus,
  setError,
  setStatus,
  submitPassCode,
} from "./redux";
import {
  PassCodeRequestPayload,
  QRCodeRequestPayload,
  QRCodeResponse,
  StatusType,
} from "./types";
import { AuthenticatorStatusEnum, PassCodeSubmissionStatusEnum } from "./enums";
import { currentFlow } from "./selectors";

export function* onLoad(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType, currentLayout } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const apiHandler = yield call(() => factory());
  try {
    const QRCode: QRCodeResponse = yield call(() =>
      currentLayout === mfaFlow.MFA
        ? apiHandler.putUpdateMFA("okta", cidpId, { factorType: "TOTP" })
        : apiHandler.putMFAEnroll(authenticatorType, activationCode)
    );
    yield put(afterOktaAuthenticatorFetch(QRCode));
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(
        afterOktaAuthenticatorFetch({ qrCodeLink: "", secretCode: "" })
      );
    } else {
      yield put(setError(true));
    }
  }
}

export function* onLoadStatus(
  action: PayloadAction<QRCodeRequestPayload>
): SagaIterator<void> {
  const { authenticatorType } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const cidpId = yield select(selectUserCidpId);
  const currentFlowSetup = yield select(currentFlow);

  yield put(setStatus(AuthenticatorStatusEnum.PROCESSING));
  const apiHandler = yield call(() => factory());
  try {
    const response: { status: StatusType } = yield call(() =>
      currentFlowSetup === mfaFlow.MFA
        ? apiHandler.getUpdateMfaStatus("okta", cidpId)
        : apiHandler.getAuthenticatorStatus(authenticatorType, activationCode)
    );
    yield put(afterStatusFetch(response.status));
  } catch (e: any) {
    if (e.statusCode === 400) {
      yield put(afterStatusFetch(AuthenticatorStatusEnum.ERROR));
    } else {
      yield put(setError(true));
    }
  }
}

export function* onPassCodeSubmission(
  action: PayloadAction<PassCodeRequestPayload>
) {
  yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.PROCESSING));
  const activationCode = yield select(selectActivationCode);

  const cidpId = yield select(selectUserCidpId);
  const currentFlowSetup = yield select(currentFlow);
  const { authenticatorType, passcode } = action.payload;
  const apiHandler = yield call(() => factory());
  try {
    yield call(() =>
      currentFlowSetup === mfaFlow.MFA
        ? apiHandler.updatePassCode("okta", cidpId, passcode)
        : apiHandler.submitAuthenticatorPassCode(
            authenticatorType,
            activationCode,
            passcode
          )
    );
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.DONE));
  } catch (e: any) {
    yield put(setError(true));
    yield put(afterPassCodeSubmission(PassCodeSubmissionStatusEnum.ERROR));
  }
}

// watcher function
export default function* oktaVerificationSaga() {
  yield takeLatest(loadQRCode.type, onLoad);
  yield takeLatest(loadStatus.type, onLoadStatus);
  yield takeLatest(submitPassCode.type, onPassCodeSubmission);
}
